
import React, { useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  chatbotName: string;
  chatbotAvatar: string;
}

const MessageList = ({ messages, isLoading, chatbotName, chatbotAvatar }: MessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);
  
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
      {messages.map((message) => (
        <div
          key={message.id}
          className={cn(
            "flex message-appear",
            message.isUser ? "justify-end" : "justify-start"
          )}
        >
          <div
            className={cn(
              "max-w-[80%] rounded-xl p-4",
              message.isUser
                ? "bg-primary text-primary-foreground rounded-tr-none"
                : "bg-secondary text-secondary-foreground rounded-tl-none"
            )}
          >
            {!message.isUser && (
              <div className="flex items-center gap-2 mb-2">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={chatbotAvatar} alt={chatbotName} />
                  <AvatarFallback>{chatbotName.substring(0, 2)}</AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium">{chatbotName}</span>
              </div>
            )}
            <div className="whitespace-pre-wrap">{message.content}</div>
            <div 
              className={cn(
                "text-xs mt-2",
                message.isUser ? "text-primary-foreground/70" : "text-secondary-foreground/70"
              )}
            >
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      ))}
      {isLoading && (
        <div className="flex justify-start message-appear">
          <div className="max-w-[80%] rounded-xl p-4 bg-secondary text-secondary-foreground rounded-tl-none">
            <div className="flex items-center gap-2 mb-2">
              <Avatar className="h-6 w-6">
                <AvatarImage src={chatbotAvatar} alt={chatbotName} />
                <AvatarFallback>{chatbotName.substring(0, 2)}</AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium">{chatbotName}</span>
            </div>
            <div className="flex space-x-2">
              <div className="h-2 w-2 bg-secondary-foreground/40 rounded-full animate-pulse delay-0"></div>
              <div className="h-2 w-2 bg-secondary-foreground/40 rounded-full animate-pulse delay-300"></div>
              <div className="h-2 w-2 bg-secondary-foreground/40 rounded-full animate-pulse delay-600"></div>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
